import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarDays } from "lucide-react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useViewScopeStore } from "../store/viewScopeStore";
import { isAssignedToUser } from "../lib/taskPermissions";
import CalendarView from "../components/tasks/CalendarView";

/**
 * Global calendar page — aggregates every tenant task (respecting task
 * visibility) into a single month calendar. Honours the top-bar
 * "Ben / Herkes" (Me / Everyone) toggle: when scope=me, only tasks
 * assigned to the current user are shown.
 */
export default function Takvim() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenant, user } = useAuthStore();
  const { scope } = useViewScopeStore();
  const isMe = scope === "me";
  const [tasks, setTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tasks", {
          params: { limit: 1000 },
        });
        // Backend returns paginated shape {items, total} for some routes;
        // list_tasks currently returns the raw list. Support both.
        setTasks(Array.isArray(data) ? data : data.items || []);
      } catch {
        setTasks([]);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const visibleTasks = useMemo(() => {
    if (!isMe || !user?.id) return tasks;
    return tasks.filter((tk) => isAssignedToUser(tk, user.id));
  }, [tasks, isMe, user?.id]);

  const openTask = (task) => {
    if (!task) return;
    // Jump to the parent project detail with the task drawer opened.
    navigate(`/app/projects/${task.project_id}?task=${task.id}`);
  };

  return (
    <div className="space-y-6" data-testid="takvim-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          {t("brand")}
        </div>
        <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          <CalendarDays
            className="h-7 w-7 text-blue-500"
            strokeWidth={1.75}
          />
          {t("nav.calendar")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {isMe
            ? "Sana atanmış tüm görevlerin aylık takvimi."
            : "Şirket genelindeki tüm görevlerin aylık takvimi."}{" "}
          Bir güne tıklayarak görevin detayını aç.
        </p>
      </div>

      {!loaded ? (
        <div className="py-20 text-center text-sm text-zinc-500">
          Yükleniyor...
        </div>
      ) : visibleTasks.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-10 text-center">
          <CalendarDays
            className="mx-auto h-10 w-10 text-zinc-300"
            strokeWidth={1.5}
          />
          <p className="mt-2 text-sm text-zinc-500">
            {isMe
              ? "Sana atanmış görev yok."
              : "Henüz takvime yansıyacak görev yok."}
          </p>
        </div>
      ) : (
        <CalendarView
          tasks={visibleTasks}
          tenantTz={tenant?.timezone}
          onOpen={openTask}
        />
      )}
    </div>
  );
}
